import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';
import { getAvailableModels } from '@/services/data.store';

// Sentinel for "no tenant default chosen" — Radix Select can't use an empty
// string value. Maps to null on save (the backend falls back to Haiku).
const PLATFORM_DEFAULT = '__default__';
const PLATFORM_DEFAULT_LABEL = 'Platform default (Claude Haiku 4.5)';

export default function EllieTab() {
  const queryClient = useQueryClient();
  const [tenantConfigId, setTenantConfigId] = useState('');
  const [allowSqlQueries, setAllowSqlQueries] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [model, setModel] = useState(PLATFORM_DEFAULT);
  const [centerInputOnNewChat, setCenterInputOnNewChat] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: registrationData, isLoading } = useQuery({
    queryKey: ['tenantRegistration'],
    queryFn: async () => {
      return await TenantConfigService.getInstance().getTenantRegistration();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: models = [] } = useQuery({
    queryKey: ['availableModels'],
    queryFn: getAvailableModels,
    staleTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    if (registrationData) {
      const { tenant_config } = registrationData;
      setTenantConfigId(tenant_config.id);
      setAllowSqlQueries(tenant_config.ellie_allow_sql_queries ?? false);
      setCustomInstructions(tenant_config.ellie_custom_instructions ?? '');
      setModel(tenant_config.ellie_model || PLATFORM_DEFAULT);
      setCenterInputOnNewChat(tenant_config.ellie_center_input_on_new_chat ?? false);
    }
  }, [registrationData]);

  const save = useCallback(async () => {
    if (!tenantConfigId) return;

    setSaving(true);
    try {
      await TenantConfigService.getInstance().saveEllieSettings(tenantConfigId, {
        ellie_allow_sql_queries: allowSqlQueries,
        ellie_custom_instructions: customInstructions,
        ellie_model: model === PLATFORM_DEFAULT ? null : model,
        ellie_center_input_on_new_chat: centerInputOnNewChat,
      });

      showToast.success('Ellie settings saved');
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      // The chat surfaces read the default model from useTenantConfig()
      queryClient.invalidateQueries({ queryKey: ['tenantConfig'] });
    } catch (err: any) {
      showToast.error('Failed to save Ellie settings', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, allowSqlQueries, customInstructions, model, centerInputOnNewChat, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>LLM Model</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Model</Label>
              <p className="text-sm text-muted-foreground">
                Used as the starting model for every new Ellie conversation.
              </p>
            </div>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PLATFORM_DEFAULT}>
                  {PLATFORM_DEFAULT_LABEL}
                </SelectItem>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SQL Queries</CardTitle>
          <CardDescription>
            Allow Ellie to run arbitrary SQL queries against your data warehouse
            to answer questions. When disabled, Ellie can only use the semantic
            layer (metrics and dimensions).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Allow SQL Queries</Label>
              <p className="text-sm text-muted-foreground">
                {allowSqlQueries
                  ? 'Ellie can run SQL queries against your warehouse'
                  : 'SQL queries are disabled'}
              </p>
            </div>
            <Switch
              checked={allowSqlQueries}
              onCheckedChange={setAllowSqlQueries}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Layout</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label>Center input on new chats</Label>
            <Switch
              checked={centerInputOnNewChat}
              onCheckedChange={setCenterInputOnNewChat}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom Instructions</CardTitle>
          <CardDescription>
            Add custom instructions that will be appended to Ellie's system
            prompt. Use this to provide business context, define terminology,
            set preferred analysis approaches, or give Ellie any other guidance
            specific to your organization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            value={customInstructions}
            onChange={(e) => setCustomInstructions(e.target.value)}
            placeholder="e.g. Our fiscal year starts in April. When analyzing revenue, always break down by product line. Use 'customers' instead of 'users' in your responses."
            rows={8}
          />
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving}>
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Saving...
          </>
        ) : (
          'Save'
        )}
      </Button>
    </div>
  );
}
